/**
 * Buyer-Demand Phase 1 / Phase 5 commit 1.5 — single buyer config view.
 *
 * The project list moved to /admin/projects (cross-buyer operational
 * view) — this page now shows buyer config only: name, contact, status,
 * notes. The legacy per-buyer HMAC secret and its rotate control were
 * removed in Phase 5 commit 1.5 alongside the column drop. The "View
 * this buyer's projects" link deep-links into the projects table with a
 * buyer_id filter.
 */
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { fetchBuyer } from '../../lib/adminApi';

export default function AdminBuyerDetail() {
  const { id = '' } = useParams<{ id: string }>();

  const { data: buyerData, isLoading } = useQuery({
    queryKey: ['admin', 'buyer', id],
    queryFn: () => fetchBuyer(id),
    enabled: !!id,
  });

  if (isLoading) return <div className="text-slate-400 text-sm">Loading…</div>;
  if (!buyerData?.buyer) return <div className="text-slate-400 text-sm">Buyer not found.</div>;

  const buyer = buyerData.buyer;

  return (
    <div className="space-y-4">
      <Link to="/admin/buyers" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white">
        <ArrowLeft className="w-3 h-3" /> Back to buyers
      </Link>

      <div className="bg-slate-800 rounded-xl p-6 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold font-display text-white">{buyer.name}</h1>
            <p className="text-sm text-slate-400 mt-1">{buyer.contact_email}</p>
            {buyer.contact_name && <p className="text-xs text-slate-500">{buyer.contact_name}</p>}
            {buyer.notes && <p className="text-sm text-slate-300 mt-3 whitespace-pre-wrap">{buyer.notes}</p>}
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            buyer.status === 'active' ? 'bg-green-900/50 text-green-400' :
            buyer.status === 'paused' ? 'bg-amber-900/50 text-amber-400' :
            'bg-slate-700 text-slate-400'
          }`}>{buyer.status}</span>
        </div>

        <div className="pt-3 border-t border-slate-700 text-xs text-slate-500">
          Created {new Date(buyer.created_at).toLocaleDateString()}
        </div>
      </div>

      <Link
        to={`/admin/projects?buyer_id=${encodeURIComponent(buyer.id)}`}
        className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
      >
        View this buyer&apos;s projects <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}
